// src/utils/merchantFrontAuth.js
//
// merchantFrontAuth: type === "merchant" kullanıcılar için rol bazlı granular yetki
// sistemi. Admin tarafındaki utils/frontAuth.js'in merchant muadilidir; rollerin
// kendisi ve "kapsam (scope)" kavramı farklıdır.
//
// Saf tutulur (React / pageConfig importu yok) ki import döngüsü oluşmasın. Sayfa
// listesi pageConfig'ten parametre olarak geçilir.
//
// Kapsam (scope) yetenekleri özel `view-all` capability'si ile modellenir:
//   - my-operations/view-all KAPALI  -> kullanıcı yalnız kendi/ekibinin operasyonlarını görür
//   - warehouse-list/view-all KAPALI -> kullanıcı yalnız sorumlusu olduğu depoları görür
// Açık olduğunda kısıt kalkar (hepsini görür). Hangi mekanizmanın (ekip vs depo)
// uygulandığı kullanıcının rolüne bağlıdır; bu yüzden modal içeriği de role göre değişir.

import { normalizeMerchantRoleKey, isWarehouseResponsibleRole } from "./warehouseResponsibleAccess";
// Saf yardımcıları admin tarafından yeniden kullan (yeniden yazma).
import {
  ALL_ACCESS,
  VIEW_CAPABILITY,
  DEFAULT_CAPABILITIES,
  can,
  canView,
  normalizeFrontAuth,
} from "./frontAuth";

export { ALL_ACCESS, can, canView, normalizeFrontAuth };

// ---------------------------------------------------------------------------
// Roller
// ---------------------------------------------------------------------------
// Etiketler AddMerchant.js / MerchantList.js'teki mevcut map'lerle aynıdır.
export const MERCHANT_ROLE_ORDER = ["admin", "opsManager", "salesRep", "accountant", "warehouseResp", "driver", "porter"];

export const MERCHANT_ROLE_HIERARCHY = {
  admin: 6,
  opsManager: 5,
  salesRep: 4,
  // Muhasebe sorumlusu: satış temsilcisiyle aynı seviyede (peer) — yönetim yalnız
  // opsManager/admin'de. Satış temsilcisi gibi ekstra bilgi (TCKN) istemez.
  accountant: 4,
  warehouseResp: 3,
  driver: 2,
  porter: 1,
};

// Bilinmeyen/eski (örn. "member") roller için geriye dönük uyum sentinel'i. Bu role sahip
// ve kayıtlı frontAuth'u olmayan merchant'lar ESKİ davranışı korur: user-permissions hariç
// tüm merchant sayfaları + tüm yetenekler (kapsam dahil) açık gelir. Böylece frontAuth
// sistemi devreye girmeden önce var olan merchant'lar kısıtlanmaz.
export const LEGACY_MERCHANT_ROLE = "member";

export const MERCHANT_ROLE_LABELS = {
  admin: "Yönetici",
  salesRep: "Satış Temsilcisi",
  accountant: "Muhasebe Sorumlusu",
  opsManager: "Operasyon Yöneticisi",
  warehouseResp: "Depo Sorumlusu",
  driver: "Şoför",
  porter: "Taşıma Personeli",
  member: "Üye",
};

// Bilinmeyen/boş rol → legacy "member" (permissive, geriye dönük uyum).
export const DEFAULT_MERCHANT_ROLE = LEGACY_MERCHANT_ROLE;

const ROLE_KEY_BY_NORMALIZED = {
  admin: "admin",
  opsmanager: "opsManager",
  salesrep: "salesRep",
  accountant: "accountant",
  muhasebe: "accountant",
  muhasebesorumlusu: "accountant",
  warehouseresp: "warehouseResp",
  warehouseresponsible: "warehouseResp",
  deposorumlusu: "warehouseResp",
  driver: "driver",
  porter: "porter",
  member: "member",
};

export function normalizeMerchantRole(role) {
  const key = normalizeMerchantRoleKey(role);
  return ROLE_KEY_BY_NORMALIZED[key] || DEFAULT_MERCHANT_ROLE;
}

// Rol hiyerarşisi kuralları (admin tarafındaki canModifyUser/assignableRoles ile aynı mantık):
// bir firma kullanıcısı yalnız KENDİNDEN DÜŞÜK seviyedeki bir kullanıcıyı düzenleyebilir ve
// yalnız kendi seviyesine kadar rol atayabilir. Kontrol yokken salesRep gibi bir rol,
// "Yetki Yönet" yetkisi verildiğinde kendini/başkasını firma admini yapabiliyordu.
export function merchantRoleLevel(role) {
  return MERCHANT_ROLE_HIERARCHY[normalizeMerchantRole(role)] || 0;
}

// Legacy/rolsüz ("member") kullanıcı hiyerarşide 0 seviyede kalıyordu: "Yetki Yönet"
// verilse bile kimseyi düzenleyemiyor, rol listesi de boş geliyordu. Legacy'nin permissive
// tasarımıyla tutarlı olsun diye YALNIZCA aktör tarafında admin seviyesinde sayılır; HEDEF
// tarafında 0 kalır ki firma admini eski/rolsüz kullanıcıya rol atayabilsin.
function merchantActorRoleLevel(role) {
  if (normalizeMerchantRole(role) === LEGACY_MERCHANT_ROLE) return MERCHANT_ROLE_HIERARCHY.admin;
  return merchantRoleLevel(role);
}

export function canModifyMerchantUser(actorRole, targetCurrentRole) {
  return merchantActorRoleLevel(actorRole) > merchantRoleLevel(targetCurrentRole);
}

export function assignableMerchantRoles(actorRole) {
  const level = merchantActorRoleLevel(actorRole);
  return MERCHANT_ROLE_ORDER.filter((r) => merchantRoleLevel(r) <= level);
}

export function isMerchantAdmin(role) {
  return normalizeMerchantRole(role) === "admin";
}

// Operasyonları "ekip" kapsamına çekilen roller. MyOperations.js'teki enforcement ile TEK
// kaynak: orası da bunu import eder (rol seti ile editör kilidi asla ayrışmasın).
// Bu roller dışındaki rollerde "Tüm Operasyonları Gör" anlamsızdır (ekip kısıtı yok) →
// kapsam zorunlu açık + kilitli gösterilir (getForcedMerchantCapabilities).
const TEAM_SCOPED_MERCHANT_ROLE_KEYS = new Set(["driver", "porter", "opsmanager"]);

export function shouldScopeMerchantOperationsToTeam(role) {
  return TEAM_SCOPED_MERCHANT_ROLE_KEYS.has(normalizeMerchantRoleKey(role));
}

// ---------------------------------------------------------------------------
// Yetenek (capability) kataloğu
// ---------------------------------------------------------------------------
export { VIEW_CAPABILITY };

export const MERCHANT_PAGE_CAPABILITIES = {
  // Genel Bakış: Yaklaşan / Yapılan Ödemeler (firmanın hakedişleri). Default YALNIZ Yönetici'de
  // açık; diğer rollere Yetkilendirme'den verilebilir. Sunucu da aynı kuralla kontrol eder
  // (payments::getMerchantPayouts → merchantOverviewAllowed).
  "merchant-overview": [VIEW_CAPABILITY],
  "my-warehouse-firm": [
    VIEW_CAPABILITY,
    { id: "edit", label: "Profil Düzenle" },
    // İşaretli değilse firma profilindeki Komisyon sekmesi hiç görünmez.
    // Default yalnız Yönetici rolünde açık (rol şablonları ALL üzerinden alır).
    { id: "commission", label: "Komisyon Oranını Gör" },
    // Müşteri Sözleşmeleri bölümü: işaretli değilse Sözleşmelerim'deki bölüm HİÇ görünmez;
    // işaretliyse görüntüleme + "Okudum, onaylıyorum". Default yalnız Yönetici'de açık.
    { id: "agreements-approve", label: "Müşteri Sözleşmeleri (Gör ve Onayla)" },
  ],
  "warehouse-list": [
    VIEW_CAPABILITY,
    { id: "view-all", label: "Tüm Depoları Gör" },
    { id: "add", label: "Depo Ekle" },
    { id: "delete", label: "Depo Sil" },
    { id: "edit", label: "Depo Düzenle" },
    { id: "price-view", label: "Fiyat Listesi Gör" },
    { id: "price-edit", label: "Fiyat Düzenle" },
    { id: "customers-view", label: "Müşterileri Gör" },
    { id: "customers-detail", label: "Müşteri Detay" },
  ],
  "my-customers": [
    VIEW_CAPABILITY,
    { id: "view-operations", label: "Operasyonlar" },
    { id: "view-warehouses", label: "Depo Bilgileri" },
    // "Sözleşmeler" aksiyonu kataloğa yoktu: canMerchant("my-customers","view-contracts")
    // hiçbir merchant rolünde (admin dahil) true dönmüyor, aksiyon herkesten gizliydi.
    { id: "view-contracts", label: "Sözleşmeler" },
  ],
  "transport-requests": [
    VIEW_CAPABILITY,
    { id: "view-offers", label: "Teklifi Gör" },
    { id: "bid", label: "Teklif Ver" },
  ],
  "my-operations": [
    VIEW_CAPABILITY,
    { id: "view-all", label: "Tüm Operasyonları Gör" },
    { id: "confirm", label: "Operasyon Onayla" },
    { id: "complete", label: "Operasyonu Tamamla" },
    { id: "report", label: "Rapor Gör" },
    { id: "team", label: "Operasyon Ekibi" },
    { id: "customer-info", label: "Müşteri Bilgileri" },
  ],
  // Sözleşmeler: yüklenen imzalı sözleşmeyi onaylama/reddetme ayrı yetkidir.
  contracts: [
    VIEW_CAPABILITY,
    // Kapsam yeteneği: depo sorumlusunda default KAPALI — yalnız sorumlu depolarının
    // operasyon sözleşmelerini görür. Diğer roller kısıtsız (kilitli açık).
    { id: "view-all", label: "Tüm Sözleşmeleri Gör" },
    { id: "approve", label: "Onayla / Reddet" },
  ],
  // Ödemeler: fatura yükleme ayrı yetkidir.
  payments: [
    VIEW_CAPABILITY,
    { id: "invoice-upload", label: "Fatura Yükle" },
    { id: "invoice-delete", label: "Fatura Sil" },
    { id: "customer-invoices", label: "Müşteri Faturalarını Gör" },
    { id: "my-invoices", label: "Faturalarım'ı Gör" },
    // Ödeme geçmişine elle borç ekleme/düzenleme/silme. Default yalnız yönetici (admin)
    // rolünde açık (ALL("payments")); salesRep yalnız "view" aldığından kapalı.
    { id: "manual-payment", label: "Manuel Borç (Ekle/Düzenle/Sil)" },
  ],
  chat: [VIEW_CAPABILITY],
  // Yetki yönetim sayfası: görüntüleme + yetki yönetme
  "user-permissions": [VIEW_CAPABILITY, { id: "manage", label: "Yetki Yönet" }],
};

export function getMerchantCapabilities(pageKey) {
  return MERCHANT_PAGE_CAPABILITIES[pageKey] || DEFAULT_CAPABILITIES;
}

export function getMerchantCapabilityIds(pageKey) {
  return getMerchantCapabilities(pageKey).map((c) => c.id);
}

export function getMerchantCapabilityLabel(pageKey, capId) {
  return getMerchantCapabilities(pageKey).find((c) => c.id === capId)?.label || capId;
}

// ---------------------------------------------------------------------------
// Bağlı (zorunlu açık) yetenekler — admin tarafındaki CAPABILITY_DEPENDENCIES'in
// merchant muadili, ama TERS yönde çalışır: burada kaynak yetenek hedefi ZORUNLU
// AÇAR (önkoşul değil, sonuç). Hedef editörde işaretli + kilitli görünür.
// İki kaynak vardır:
//   1) Kapsam (view-all) kilitleri — ROLE bağlı (getForcedMerchantCapabilities içinde):
//      rolün ilgili kısıt mekanizması yoksa "tümünü gör" anlamsızdır → zorunlu açık.
//   2) Statik implikasyonlar (aşağıdaki tablo) — yetenek bazlı, her zaman geçerli.
// ---------------------------------------------------------------------------
//   { [pageKey]: { [srcCapId]: [{ page, cap }] } }
export const MERCHANT_CAPABILITY_IMPLICATIONS = {
  "warehouse-list": {
    // Fiyat düzenleyebilen, fiyat listesini de görebilmeli.
    "price-edit": [{ page: "warehouse-list", cap: "price-view" }],
    // Müşteri detayı açıkken liste görünümü de zorunlu açık.
    "customers-detail": [{ page: "warehouse-list", cap: "customers-view" }],
  },
  "transport-requests": {
    // Teklif verebilen, verdiği teklifi de görebilmeli ("Teklifi Gör" tek başına da açılır).
    bid: [{ page: "transport-requests", cap: "view-offers" }],
  },
};

// Kapsam kilidi açıklamaları (role bağlı; mekanizma yoksa zorunlu açık).
const SCOPE_LOCK_MESSAGES = {
  "warehouse-list": "Bu rolde depo görüntüleme kısıtı bulunmadığından tüm depolar görülür; kapatılamaz.",
  "my-operations": "Bu rolde operasyon ekibi kısıtı bulunmadığından tüm operasyonlar görülür; kapatılamaz.",
  contracts: "Bu rolde sözleşme görüntüleme kısıtı bulunmadığından tüm sözleşmeler görülür; kapatılamaz.",
};

// Verili rol + mevcut yetkiler için ZORUNLU AÇIK (kilitli) yetenekleri hesaplar.
// Dönüş: { [pageKey]: { [capId]: aciklamaMetni } }
export function getForcedMerchantCapabilities(role, permissions) {
  const forced = {};
  const add = (pageKey, capId, message) => {
    if (!forced[pageKey]) forced[pageKey] = {};
    if (!forced[pageKey][capId]) forced[pageKey][capId] = message;
  };

  // 1) Kapsam (view-all) kilitleri — yalnız sayfa (view) açıkken anlamlı.
  if (can(permissions, "warehouse-list", "view") && !isWarehouseResponsibleRole(role)) {
    add("warehouse-list", "view-all", SCOPE_LOCK_MESSAGES["warehouse-list"]);
  }
  if (can(permissions, "my-operations", "view") && !shouldScopeMerchantOperationsToTeam(role)) {
    add("my-operations", "view-all", SCOPE_LOCK_MESSAGES["my-operations"]);
  }
  // Sözleşme kapsamı yalnız depo sorumlusunda seçime açıktır; diğer rollerde kilitli açık.
  if (can(permissions, "contracts", "view") && !isWarehouseResponsibleRole(role)) {
    add("contracts", "view-all", SCOPE_LOCK_MESSAGES["contracts"]);
  }

  // 2) Statik implikasyonlar — kaynak yetenek açıkken hedef zorunlu açık.
  for (const [pageKey, capMap] of Object.entries(MERCHANT_CAPABILITY_IMPLICATIONS)) {
    for (const [srcCap, targets] of Object.entries(capMap)) {
      if (!can(permissions, pageKey, srcCap)) continue;
      const srcLabel = getMerchantCapabilityLabel(pageKey, srcCap);
      for (const t of targets) {
        add(t.page, t.cap, `"${srcLabel}" açıkken zorunlu açıktır.`);
      }
    }
  }
  return forced;
}

// Zorunlu açık yetenekleri permissions'a EKLER (asla kaldırmaz). Editörde her mutasyondan
// sonra çağrılır → tutarlı durum garanti edilir. Zincirleme için sabit noktaya kadar tekrarlar.
export function applyMerchantImplications(permissions, role) {
  if (!permissions || typeof permissions !== "object") return permissions;
  let result = { ...permissions };
  let changed = true;
  while (changed) {
    changed = false;
    const forced = getForcedMerchantCapabilities(role, result);
    for (const [pageKey, capMap] of Object.entries(forced)) {
      const current = Array.isArray(result[pageKey]) ? result[pageKey] : [];
      for (const capId of Object.keys(capMap)) {
        if (!current.includes(capId)) {
          result = { ...result, [pageKey]: [...(result[pageKey] || []), capId] };
          changed = true;
        }
      }
    }
  }
  return result;
}

// ---------------------------------------------------------------------------
// Önkoşul bağımlılıkları — admin tarafındaki CAPABILITY_DEPENDENCIES'in birebir
// muadili (implikasyonun TERSİ): bir yetenek başka bir sayfaya yönlendirdiği için
// yalnız hedef (page, cap) AÇIKKEN verilebilir. Hedef kapanınca bağımlı yetki düşer
// ve editörde tekrar açılamaz (kilitli görünür).
//   { [pageKey]: { [capId]: { page, cap } } }
// ---------------------------------------------------------------------------
export const MERCHANT_CAPABILITY_DEPENDENCIES = {
  "my-customers": {
    // "Operasyonlar" aksiyonu Operasyonlar sayfasını açar → my-operations görünür olmalı.
    "view-operations": { page: "my-operations", cap: "view" },
  },
};

export function getMerchantCapabilityDependency(pageKey, capId) {
  return MERCHANT_CAPABILITY_DEPENDENCIES[pageKey]?.[capId] || null;
}

export function isMerchantCapabilityDependencyMet(permissions, pageKey, capId) {
  const dep = getMerchantCapabilityDependency(pageKey, capId);
  if (!dep) return true;
  return can(permissions, dep.page, dep.cap);
}

// Önkoşulu karşılanmayan tüm yetenekleri kaldırır (zincirleme için sabit noktaya kadar tekrarlar).
export function applyMerchantCapabilityDependencies(permissions) {
  if (!permissions || typeof permissions !== "object") return permissions;
  let result = { ...permissions };
  let changed = true;
  while (changed) {
    changed = false;
    for (const [pageKey, deps] of Object.entries(MERCHANT_CAPABILITY_DEPENDENCIES)) {
      for (const capId of Object.keys(deps)) {
        const caps = result[pageKey];
        if (!Array.isArray(caps) || !caps.includes(capId)) continue;
        if (!isMerchantCapabilityDependencyMet(result, pageKey, capId)) {
          result = { ...result, [pageKey]: caps.filter((c) => c !== capId) };
          changed = true;
        }
      }
    }
  }
  return result;
}

// Editörün her mutasyonda çağırdığı birleşik normalizasyon: önce zorunlu-açıkları EKLE
// (implikasyon + kapsam kilidi), sonra önkoşulu karşılanmayanları DÜŞÜR. İki küme ayrıktır
// (biri ekler, diğeri kaldırır; ortak yetenek yok) → tek geçiş yeterli, ama her ikisi de
// kendi içinde sabit noktaya kadar döner.
export function normalizeMerchantPermissions(permissions, role) {
  return applyMerchantCapabilityDependencies(applyMerchantImplications(permissions, role));
}

// ---------------------------------------------------------------------------
// Rol default yetki şablonları (hardcoded; UI'da düzenlenebilir başlangıç şablonu)
// ---------------------------------------------------------------------------
// Mevcut sabit davranışı korur:
//   - admin/salesRep -> my-operations'ta view-all (tüm operasyonlar)
//   - opsManager/driver/porter -> my-operations'ta view-all YOK (ekip kapsamı)
//   - warehouseResp -> warehouse-list'te view-all YOK (sadece sorumlu depolar)
// ⚠️ ÇİFT TABLO: firma bildirimlerinin alıcıları BE'de bu tablonun kopyasıyla hesaplanır
// (notifications.php::MERCHANT_ROLE_DEFAULTS + COMPANY_NOTIFICATION_MODULES). Burası
// değişirse orası da güncellenmeli; yoksa kişi sayfayı görür ama bildirimini almaz (ya da tersi).
const ALL = (pageKey) => getMerchantCapabilityIds(pageKey);

export const MERCHANT_ROLE_DEFAULTS = {
  admin: {
    "merchant-overview": ALL("merchant-overview"),
    "my-warehouse-firm": ALL("my-warehouse-firm"),
    "warehouse-list": ALL("warehouse-list"),
    "my-customers": ALL("my-customers"),
    "transport-requests": ALL("transport-requests"),
    "my-operations": ALL("my-operations"),
    contracts: ALL("contracts"),
    payments: ALL("payments"),
    chat: ALL("chat"),
    "user-permissions": ALL("user-permissions"),
  },
  salesRep: {
    "warehouse-list": ["view", "view-all", "customers-view", "customers-detail"],
    "my-customers": ALL("my-customers"),
    "transport-requests": ALL("transport-requests"),
    "my-operations": ["view", "view-all", "report", "customer-info"],
    contracts: ["view"],
    payments: ["view"],
    chat: ALL("chat"),
  },
  // Muhasebe sorumlusu: default YALNIZ müşteri yönetimi + mesajlaşma açık.
  accountant: {
    "my-customers": ALL("my-customers"),
    chat: ALL("chat"),
  },
  opsManager: {
    // Ekip kapsamı: view-all YOK. Onaylama yetkisi yönetim rolünde açık.
    "my-operations": ["view", "confirm", "complete", "report", "team", "customer-info"],
    contracts: ["view", "approve"],
    chat: ALL("chat"),
  },
  warehouseResp: {
    // Sadece sorumlu depolar: warehouse-list/view-all YOK
    "warehouse-list": ["view", "edit", "price-view", "customers-view"],
    "my-operations": ["view", "report", "team", "customer-info"],
    // Sözleşmeler: default yalnız SORUMLU depolarının sözleşmeleri (view-all bilinçli yok).
    contracts: ["view"],
    chat: ALL("chat"),
  },
  driver: {
    // Ekip kapsamı: view-all YOK
    "my-operations": ["view", "report"],
    chat: ALL("chat"),
  },
  porter: {
    "my-operations": ["view", "report"],
    chat: ALL("chat"),
  },
};

// Legacy "member": user-permissions ve merchant-overview hariç tüm sayfalar + tüm yetenekler
// (kapsam dahil). Yetki yönetimi (user-permissions) hariç tutulur ki eski merchant'lar
// Yetkilendirme'yi göremesin; Genel Bakış (hakedişler) default yalnız Yönetici'dedir.
// my-warehouse-firm ayrıca pageConfig'te admin-only kalmaya devam eder.
const LEGACY_EXCLUDED_PAGES = new Set(["user-permissions", "merchant-overview"]);
MERCHANT_ROLE_DEFAULTS[LEGACY_MERCHANT_ROLE] = Object.fromEntries(
  Object.keys(MERCHANT_PAGE_CAPABILITIES)
    .filter((key) => !LEGACY_EXCLUDED_PAGES.has(key))
    .map((key) => [key, getMerchantCapabilityIds(key)])
);

export function buildMerchantDefaultPermissions(role) {
  const normalizedRole = normalizeMerchantRole(role);
  const template = MERCHANT_ROLE_DEFAULTS[normalizedRole] || {};
  // Kopya döndür (çağıran mutasyona uğratabilir)
  const out = {};
  for (const [key, caps] of Object.entries(template)) {
    if (Array.isArray(caps) && caps.length) out[key] = [...caps];
  }
  return out;
}

// ---------------------------------------------------------------------------
// Modalın role-bağlı içeriği
// ---------------------------------------------------------------------------
// Bir role hangi sayfaların/yeteneklerin matriste gösterileceğini belirler. Default
// şablonundaki sayfalar + her zaman görünür "chat" gösterilir; böylece scope toggle'ı
// (view-all) yalnız ilgili rolde belirir.
//
// merchantPages: pageConfig.getMerchantPages() çıktısı [{ key, text, title, path, capabilities }]
// Kayıtlı yetkide BULUNMAYAN sayfa anahtarlarını rol varsayılanından tamamlar.
// (Açıkça kaldırılmış yetkiler — anahtar VAR, dizi boş — olduğu gibi korunur.)
// Tek kaynak: hem sidebar (pageConfig.usePageList) hem rota kapısı (useMerchantFrontAuth)
// bunu kullanır; ayrı ayrı yazıldığında menü ile rota kararı ayrışıyordu.
export function mergeMissingMerchantPagePermissions(stored, role) {
  if (!stored || typeof stored !== "object") return stored;
  const defaults = buildMerchantDefaultPermissions(role);
  let merged = null;
  for (const key of Object.keys(defaults)) {
    if (key in stored) continue;
    if (!merged) merged = { ...stored };
    merged[key] = defaults[key];
  }
  // YÖNETİCİ (admin) rolü tasarım gereği TAM yetkidir: eski kayıtlarda sonradan eklenen
  // YETENEKLER (ör. warehouse-list/delete, contracts/approve) sayfa anahtarı var diye
  // eksik kalıyordu. Admin'de her sayfanın yetenekleri varsayılanla BİRLEŞTİRİLİR;
  // diğer rollerde kayıtlı kısıtlamalar (bilinçli kapatılmış yetenekler) korunur.
  if (normalizeMerchantRole(role) === "admin") {
    for (const [key, caps] of Object.entries(defaults)) {
      const current = (merged || stored)[key];
      if (!Array.isArray(current) || !Array.isArray(caps)) continue;
      const missing = caps.filter((c) => !current.includes(c));
      if (missing.length === 0) continue;
      if (!merged) merged = { ...stored };
      merged[key] = [...current, ...missing];
    }
  }
  return merged || stored;
}

export function getMerchantPagesForRole(role, merchantPages = []) {
  const normalizedRole = normalizeMerchantRole(role);
  // admin her şeyi yönetebilir
  if (normalizedRole === "admin") return merchantPages;

  const allowedKeys = new Set(Object.keys(MERCHANT_ROLE_DEFAULTS[normalizedRole] || {}));
  allowedKeys.add("chat");
  return merchantPages.filter((page) => allowedKeys.has(page.key));
}

// ---------------------------------------------------------------------------
// Yetki yönetim sayfası erişimi
// ---------------------------------------------------------------------------
// merchant-admin'e güvenlik kemeri + user-permissions/manage kontrolü.
export function canManageMerchantPermissions(frontAuth, role) {
  if (frontAuth === ALL_ACCESS) return true;
  if (isMerchantAdmin(role)) return true; // merchant-admin her zaman yönetebilir
  return can(frontAuth, "user-permissions", "manage");
}

// Operasyon kapsamı için yardımcı: kullanıcı yalnız ekip/sorumlu kapsamıyla mı sınırlı?
// (Mekanizmayı çağıran taraf rolü ile birlikte uygular; bu yalnız "tümünü gör" kararıdır.)
export function canViewAllOperations(frontAuth) {
  return can(frontAuth, "my-operations", "view-all");
}

export function canViewAllWarehouses(frontAuth) {
  return can(frontAuth, "warehouse-list", "view-all");
}

export { isWarehouseResponsibleRole };
